<template>
  <div class="map-container">
    <div class="map-wrapper">
      <div id="tibet-map" ref="mapRef"></div>
    </div>
    
    <el-drawer
      v-model="drawerVisible"
      :title="selectedRegion?.name || '区域详情'"
      direction="rtl"
      size="50%"
    >
      <div v-if="selectedRegion" class="region-detail">
        <el-tabs v-model="activeTab">
          <el-tab-pane label="藏戏简介 བོད་ཞབས་རགས་བཤད།" name="intro">
            <div class="content-section">
              <h3>{{ selectedRegion.name }}藏戏</h3>
              <p>{{ selectedRegion.introduction }}</p>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="传承人简介 བརྒྱུད་འཛིན་པའི་ངོ་སྤྲོད།" name="inheritor">
            <div class="content-section">
              <h3>传承人信息</h3>
              <div v-for="(person, index) in selectedRegion.inheritors" :key="index" class="inheritor-card">
                <img :src="person.avatar" :alt="person.name" class="avatar">
                <div class="info">
                  <h4>{{ person.name }}</h4>
                  <p>{{ person.description }}</p>
                </div>
              </div>
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="图片展示 པར་སྟོན།" name="images">
            <div class="image-gallery">
              <el-image 
                v-for="(img, index) in selectedRegion.images" 
                :key="index"
                :src="img"
                :preview-src-list="selectedRegion.images"
                fit="cover"
                class="gallery-image"
              />
            </div>
          </el-tab-pane>
          
          <el-tab-pane label="视频展示 བརྙན་སྟོན།" name="videos">
            <div class="video-section">
              <div v-for="(video, index) in selectedRegion.videos" :key="index">
                <video 
                  :src="video.url"
                  controls
                  class="video-player"
                >
                  您的浏览器不支持视频播放
                </video>
                <p class="video-title">{{ video.title }}</p>
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import * as echarts from 'echarts'
import { getRegionDetail } from '@/api'
import { ElMessage } from 'element-plus'

import xizangPrefectures7 from '@/assets/maps/xzzs.json'

const mapRef = ref(null)
const drawerVisible = ref(false)
const selectedRegion = ref(null)
const activeTab = ref('intro')
let chartInstance = null

const regionNameToId = {
  '拉萨市': 1,
  '日喀则市': 2,
  '山南市': 3,
  '林芝市': 4,
  '昌都市': 5,
  '那曲市': 6,
  '阿里地区': 7
}

const regionNameToTibetan = {
  '拉萨市': 'ལྷ་ས།',
  '日喀则市': 'གཞིས་ཀ་རྩེ།',
  '山南市': 'ལྷོ་ཁ།',
  '林芝市': 'ཉིང་ཁྲི།',
  '昌都市': 'ཆབ་མདོ།',
  '那曲市': 'ནག་ཆུ།',
  '阿里地区': 'མངའ་རིས།'
}

const initMap = () => {
  chartInstance = echarts.init(mapRef.value)
  
  echarts.registerMap('tibet', xizangPrefectures7)
  
  const option = {
    title: {
      text: '西藏藏戏文化地图',
      left: 'center',
      top: 20,
      textStyle: {
        color: '#8B4513',
        fontSize: 28,
        fontWeight: 'bold'
      }
    },
    tooltip: {
      trigger: 'item',
      formatter: (params) => {
        const cn = params.name || ''
        const bo = regionNameToTibetan[cn] || ''
        return bo ? `${cn}<br/>${bo}` : cn
      }
    },
    visualMap: {
      type: 'piecewise',
      show: true,
      left: 20,
      bottom: 20,
      itemWidth: 18,
      itemHeight: 12,
      textStyle: {
        color: '#333'
      },
      pieces: [
        { value: 1, label: '拉萨市', color: '#f6d365' },
        { value: 2, label: '日喀则市', color: '#fda085' },
        { value: 3, label: '山南市', color: '#a1c4fd' },
        { value: 4, label: '林芝市', color: '#c2e9fb' },
        { value: 5, label: '昌都市', color: '#d4fc79' },
        { value: 6, label: '那曲市', color: '#84fab0' },
        { value: 7, label: '阿里地区', color: '#fccb90' }
      ]
    },
    series: [
      {
        name: '地区',
        type: 'map',
        map: 'tibet',
        roam: true,
        selectedMode: false,
        label: {
          show: true,
          formatter: (params) => {
            const cn = params.name || ''
            const bo = regionNameToTibetan[cn] || ''
            return bo ? `{cn|${cn}}\n{bo|${bo}}` : `{cn|${cn}}`
          },
          rich: {
            cn: {
              color: '#222',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 16
            },
            bo: {
              color: '#555',
              fontSize: 11,
              lineHeight: 14
            }
          }
        },
        itemStyle: {
          areaColor: '#f3f3f3',
          borderColor: '#999',
          borderWidth: 1
        },
        emphasis: {
          label: {
            color: '#8B4513',
            fontWeight: 'bold',
            fontSize: 14
          },
          itemStyle: {
            areaColor: '#FFE4B5',
            borderColor: '#8B4513',
            borderWidth: 2
          }
        },
        data: Object.keys(regionNameToId).map((name, idx) => ({
          name,
          value: idx + 1
        }))
      }
    ]
  }
  
  chartInstance.setOption(option)
  
  chartInstance.on('click', async (params) => {
    if (params.componentType === 'series' && params.seriesType === 'map') {
      try {
        const id = regionNameToId[params.name]
        if (!id) {
          ElMessage.warning('该地区暂无数据')
          return
        }
        const res = await getRegionDetail(id)
        selectedRegion.value = res.data
        drawerVisible.value = true
        activeTab.value = 'intro'
      } catch (error) {
        ElMessage.error('获取区域详情失败')
      }
    }
  })
}

onMounted(() => {
  nextTick(() => {
    initMap()
  })
  
  window.addEventListener('resize', () => {
    chartInstance?.resize()
  })
})
</script>

<style scoped lang="scss">
.map-container {
  width: 100%;
  height: calc(100vh - 110px);
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.map-wrapper {
  width: 100%;
  height: 100%;
  
  #tibet-map {
    width: 100%;
    height: 100%;
  }
}

.region-detail {
  .content-section {
    padding: 20px;
    
    h3 {
      color: #8B4513;
      margin-bottom: 15px;
      font-size: 20px;
    }
    
    p {
      line-height: 1.8;
      color: #333;
      text-indent: 2em;
    }
    
    .inheritor-card {
      display: flex;
      gap: 20px;
      margin-bottom: 20px;
      padding: 15px;
      background: #f9f9f9;
      border-radius: 8px;
      
      .avatar {
        width: 80px;
        height: 80px;
        border-radius: 50%;
        object-fit: cover;
      }
      
      .info {
        flex: 1;
        
        h4 {
          color: #8B4513;
          margin-bottom: 8px;
        }
        
        p {
          color: #666;
          font-size: 14px;
          text-indent: 0;
        }
      }
    }
  }
  
  .image-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 15px;
    padding: 20px;
    
    .gallery-image {
      width: 100%;
      height: 200px;
      border-radius: 8px;
      cursor: pointer;
    }
  }
  
  .video-section {
    padding: 20px;
    
    .video-player {
      width: 100%;
      max-height: 400px;
      border-radius: 8px;
      margin-bottom: 10px;
    }
    
    .video-title {
      color: #666;
      font-size: 14px;
      margin-bottom: 20px;
    }
  }
}
</style>
